import React, { useState, useEffect, useContext } from 'react'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc' // Import UTC plugin
import timezone from 'dayjs/plugin/timezone' // Import Timezone plugin
import { ThemeContext } from '../../context/2025/ThemeContext' // Adjust path if needed

// Extend dayjs with the plugins
dayjs.extend(utc)
dayjs.extend(timezone)

// No longer using END_DATE_COUNTDOWN
// const END_DATE_COUNTDOWN = '05/01/2025'

export default function Countdown() {
  const { isDarkMode } = useContext(ThemeContext)

  // --- Define Target Time: 6:00 PM Today in Toronto ---
  const targetTime = dayjs()
    .tz('America/Toronto') // Set the timezone explicitly
    .add(1, 'day') // Move to tomorrow's date
    .set('hour', 16) // Set hour to 16 (4 PM)
    .set('minute', 0)
    .set('second', 0)
    .set('millisecond', 0)

  // --- Function to Calculate Initial Time Left ---
  const calculateInitialTimeLeft = () => {
    const now = dayjs().tz('America/Toronto')
    if (now.isAfter(targetTime)) {
      // If target time has passed, return all zeros
      return { days: 0, hours: 0, minutes: 0, seconds: 0 }
    }
    const totalSecondsLeft = targetTime.diff(now, 'second')
    const days = Math.floor(totalSecondsLeft / (60 * 60 * 24))
    const hours = Math.floor((totalSecondsLeft % (60 * 60 * 24)) / (60 * 60))
    const minutes = Math.floor((totalSecondsLeft % (60 * 60)) / 60)
    const seconds = totalSecondsLeft % 60
    return { days, hours, minutes, seconds }
  }

  // --- Initialize State using the function ---
  const initialTime = calculateInitialTimeLeft()
  const [daysLeft, setDaysLeft] = useState(initialTime.days)
  const [hoursLeft, setHoursLeft] = useState(initialTime.hours)
  const [minutesLeft, setMinutesLeft] = useState(initialTime.minutes)
  const [secondsLeft, setSecondsLeft] = useState(initialTime.seconds) // Add state for seconds

  // --- Update the time left every second ---
  useEffect(() => {
    const intervalId = setInterval(() => {
      const now = dayjs().tz('America/Toronto')

      if (now.isAfter(targetTime)) {
        // Target time reached or passed
        setDaysLeft(0)
        setHoursLeft(0)
        setMinutesLeft(0)
        setSecondsLeft(0)
        clearInterval(intervalId) // Stop the interval
        return
      }

      // Calculate remaining time
      const totalSecondsLeft = targetTime.diff(now, 'second')
      const updatedDaysLeft = Math.floor(totalSecondsLeft / (60 * 60 * 24))
      const updatedHoursLeft = Math.floor((totalSecondsLeft % (60 * 60 * 24)) / (60 * 60))
      const updatedMinutesLeft = Math.floor((totalSecondsLeft % (60 * 60)) / 60)
      const updatedSecondsLeft = totalSecondsLeft % 60 // Calculate seconds

      // Update state
      setDaysLeft(updatedDaysLeft)
      setHoursLeft(updatedHoursLeft)
      setMinutesLeft(updatedMinutesLeft)
      setSecondsLeft(updatedSecondsLeft) // Update seconds state
    }, 1000) // Update every 1 second

    // Cleanup on component unmount
    return () => clearInterval(intervalId)
  }, [targetTime]) // Dependency array includes targetTime

  // --- Format numbers with leading zero ---
  const formatTime = (unit) => String(unit).padStart(2, '0')

  // --- JSX Structure (Preserved with addition of seconds) ---
  return (
    <div className='body-text-sm w-full flex flex-col lg:flex-row justify-around lg:justify-between items-center h-[60px] lg:h-[40px] px-6 lg:px-20'>
      {/* Left side text remains the same */}
      <div
        className={`font-normal ${
          isDarkMode ? 'text-white' : 'text-black'
        } transition-colors duration-300`}
      >
        May
        <span
          className={`${
            isDarkMode ? 'text-lime' : 'text-black font-bold'
          } transition-colors duration-300`}
        >
          {' 1-2 '}
        </span>
        YES! Public Exhibition
      </div>

      {/* Right side countdown - ADDED SECONDS */}
      <div
        className={`font-normal flex ${
          isDarkMode ? 'text-white' : 'text-black'
        } transition-colors duration-300`}
      >
        {/* Days */}
        <span
          className={`${
            isDarkMode ? 'text-lime' : 'text-black font-bold'
          } transition-colors duration-300`}
        >
          {formatTime(daysLeft)}
        </span>
        <span
          className={`mx-1 ${
            isDarkMode ? 'text-white' : 'text-black'
          } transition-colors duration-300`}
        >
          days
        </span>

        {/* Hours */}
        <span
          className={`${
            isDarkMode ? 'text-lime' : 'text-black font-bold'
          } transition-colors duration-300`}
        >
          {formatTime(hoursLeft)}
        </span>
        <span
          className={`mx-1 ${
            isDarkMode ? 'text-white' : 'text-black'
          } transition-colors duration-300`}
        >
          hours
        </span>

        {/* Minutes */}
        <span
          className={`${
            isDarkMode ? 'text-lime' : 'text-black font-bold'
          } transition-colors duration-300`}
        >
          {formatTime(minutesLeft)}
        </span>
        <span
          className={`mx-1 ${
            isDarkMode ? 'text-white' : 'text-black'
          } transition-colors duration-300`}
        >
          minutes
        </span>

        {/* Seconds - ADDED */}
        <span
          className={`${
            isDarkMode ? 'text-lime' : 'text-black font-bold'
          } transition-colors duration-300`}
        >
          {formatTime(secondsLeft)}
        </span>
        <span
          className={`mx-1 ${
            isDarkMode ? 'text-white' : 'text-black'
          } transition-colors duration-300`}
        >
          seconds
        </span>
        {/* --- End of Added Seconds --- */}

        <span>until the show</span>
      </div>
    </div>
  )
}
